import React from 'react';
import type { Showcase, ShowcaseProperty } from '../../types';

const EPC_BANDS = [
  { letter: 'A', min: 92, max: 100, colour: '#008054' },
  { letter: 'B', min: 81, max: 91, colour: '#19b459' },
  { letter: 'C', min: 69, max: 80, colour: '#8dce46' },
  { letter: 'D', min: 55, max: 68, colour: '#ffd500' },
  { letter: 'E', min: 39, max: 54, colour: '#fcaa65' },
  { letter: 'F', min: 21, max: 38, colour: '#ef8023' },
  { letter: 'G', min: 1, max: 20, colour: '#e9153b' },
] as const;

function getBand(rating: number) {
  return EPC_BANDS.find((b) => rating >= b.min && rating <= b.max) || EPC_BANDS[6];
}

interface EpcSectionProps {
  showcase: Showcase;
  property: ShowcaseProperty;
  brandColour: string;
  darkMode?: boolean;
}

export default function EpcSection({ property, darkMode: dm }: EpcSectionProps) {
  const { epc_current, epc_potential } = property;
  if (!epc_current && !epc_potential) return null;

  const currentBand = epc_current ? getBand(epc_current) : null;
  const potentialBand = epc_potential ? getBand(epc_potential) : null;

  const widths = ['30%', '37%', '44%', '51%', '58%', '65%', '72%'];

  return (
    <section className={`py-12 md:py-16 ${dm ? 'bg-slate-900' : 'bg-white'}`}>
      <div className="max-w-5xl mx-auto px-8">
        <h2 className={`text-2xl font-bold mb-8 ${dm ? 'text-white' : 'text-slate-900'}`}>Energy Performance</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {epc_current && currentBand && (
            <div>
              <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Current Rating</h3>
              <EpcChart rating={epc_current} activeBand={currentBand} widths={widths} darkMode={dm} />
            </div>
          )}
          {epc_potential && potentialBand && (
            <div>
              <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Potential Rating</h3>
              <EpcChart rating={epc_potential} activeBand={potentialBand} widths={widths} darkMode={dm} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function EpcChart({
  rating,
  activeBand,
  widths,
  darkMode: dm,
}: {
  rating: number;
  activeBand: (typeof EPC_BANDS)[number];
  widths: string[];
  darkMode?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      {EPC_BANDS.map((band, i) => {
        const isActive = band.letter === activeBand.letter;
        return (
          <div key={band.letter} className="flex items-center gap-2">
            <div
              className="relative flex items-center rounded-r-md transition-all"
              style={{
                width: widths[i],
                backgroundColor: band.colour,
                height: isActive ? '36px' : '28px',
                boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.15)' : undefined,
              }}
            >
              <span className={`px-2.5 font-bold text-white ${isActive ? 'text-base' : 'text-sm'}`}>
                {band.letter}
              </span>
              <span className={`ml-auto pr-2.5 text-white/80 ${isActive ? 'text-sm' : 'text-xs'}`}>
                {band.min}–{band.max}
              </span>
            </div>
            {isActive && (
              <div className="flex items-center gap-1.5">
                <span className={`text-2xl font-bold ${dm ? 'text-white' : 'text-slate-900'}`}>{rating}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
